import React, { useRef } from 'react';
import { UploadCloudIcon, FileIcon, Trash2Icon, FileTextIcon, HeadphonesIcon } from 'lucide-react';
import type { EditionFile } from './types';
import { fileAccessLevels, acceptedFileTypes } from './constants';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
interface EditionFileUploadProps {
  editionId: string;
  files: EditionFile[];
  formatType: string;
  onAddFile: (editionId: string, file: File, accessLevel: EditionFile['accessLevel'], versionName: string) => void;
  onUpdateFile: (editionId: string, fileId: string, field: keyof EditionFile, value: any) => void;
  onRemoveFile: (editionId: string, fileId: string) => void;
}
export const EditionFileUpload: React.FC<EditionFileUploadProps> = ({
  editionId,
  files,
  formatType,
  onAddFile,
  onUpdateFile,
  onRemoveFile
}) => {
  const dk = useDarkClasses();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // Default to private access and 'Final' version
      onAddFile(editionId, file, 'private', 'Final');
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  const formatAcceptedTypes = acceptedFileTypes[formatType] || [];
  const acceptString = formatAcceptedTypes.join(',');
  const getFormatIcon = () => {
    if (formatType === 'audiobook') return <HeadphonesIcon className="w-8 h-8 mx-auto mb-2 text-purple-400" data-id="element-20317" data-mp="2pws7yrvlbrnz" />;
    return <FileTextIcon className="w-8 h-8 mx-auto mb-2 text-purple-400" data-id="element-20318" data-mp="2lxrp59d44p0z" />;
  };
  const getFormatHelperText = () => {
    if (formatType === 'audiobook') return 'Upload audio file or provide external link below';
    if (formatType === 'paperback' || formatType === 'hardcover') return 'Optional: Upload preview/sample PDF';
    return `Accepted formats: ${formatAcceptedTypes.join(', ')} (Max 100MB)`;
  };
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  return <div className="mt-4 space-y-4" data-id="element-20319" data-mp="pc1wccz53llr">
      <div className="flex items-center justify-between" data-id="element-20320" data-mp="u923ysbn0wez">
        <h4 className={`text-sm font-semibold ${dk.textPrimary}`} data-id="element-20321" data-mp="u9jy9jeqp0k9">
          {formatType === 'audiobook' ? 'Audio Files' : 'Manuscript / Reading Files'}
        </h4>
      </div>

      {/* Upload Zone */}
      <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${dk.isDark ? 'border-gray-600 hover:border-purple-500 bg-gray-800/50' : 'border-gray-300 hover:border-purple-500 bg-gray-50'}`} onClick={() => fileInputRef.current?.click()} data-id="element-20322" data-mp="180p26xp54f0e">
        <input type="file" ref={fileInputRef} className="hidden" accept={acceptString} onChange={handleFileSelect} data-id="element-20323" data-mp="1l5zv2jqo286" />
        {getFormatIcon()}
        <p className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-20324" data-mp="3l7y6zxdyvpc5">
          Click to upload file
        </p>
        <p className={`text-xs mt-1 ${dk.textMuted}`} data-id="element-20325" data-mp="321xhicikzwr">
          {getFormatHelperText()}
        </p>
      </div>

      {/* File List */}
      {files.length > 0 && <div className="space-y-3 mt-4" data-id="element-20326" data-mp="36iihekq69py1">
          {files.map(file => <div key={file.id} className={`p-3 rounded-lg border flex flex-col sm:flex-row sm:items-center gap-3 ${dk.border} ${dk.surfaceMuted}`} data-id="element-20327" data-mp="36gfsy54o44vf">
              <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-20328" data-mp="3g9mvli80x6rt">
                <div className={`p-2 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 shrink-0`} data-id="element-20329" data-mp="3bgk9tw9hn1m6">
                  <FileIcon className="w-5 h-5" data-id="element-20330" data-mp="2it7nnn78ovte" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-20331" data-mp="2iqv9zm8il7m5">
                  <p className={`text-sm font-medium truncate ${dk.textPrimary}`} title={file.fileName} data-id="element-20332" data-mp="2wgg69ztw2flb">
                    {file.fileName}
                  </p>
                  <p className={`text-xs ${dk.textMuted}`} data-id="element-20333" data-mp="36d2qcmee52ma">
                    {file.fileType.toUpperCase()} • {formatFileSize(file.size)}{' '}
                    • Uploaded {file.uploadDate}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:w-auto w-full" data-id="element-20334" data-mp="3k4zeygguoudx">
                <input type="text" value={file.versionName} onChange={e => onUpdateFile(editionId, file.id, 'versionName', e.target.value)} placeholder="Version (e.g. Final, ARC)" className={`w-28 text-xs p-1.5 rounded border ${dk.inputOnCard}`} data-id="element-20335" data-mp="3k72d5ns5pttq" />

                <select value={file.accessLevel} onChange={e => onUpdateFile(editionId, file.id, 'accessLevel', e.target.value)} className={`w-36 text-xs p-1.5 rounded border ${dk.inputOnCard}`} data-id="element-20336" data-mp="gw48hdha30ww">
                  {fileAccessLevels.map(level => <option key={level.value} value={level.value} data-id="element-20337" data-mp="vsbt2ewlm4cp">
                      {level.label}
                    </option>)}
                </select>

                <button onClick={() => onRemoveFile(editionId, file.id)} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors shrink-0" title="Remove file" data-id="element-20338" data-mp="lsky6c44lnc3">
                  <Trash2Icon className="w-4 h-4" data-id="element-20339" data-mp="380933hakshbe" />
                </button>
              </div>
            </div>)}
        </div>}
    </div>;
};